import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CompetenciaTrackerPage.css';

const POSTS_PER_PAGE = 30;

const CompetenciaTrackerPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedCompetitors, setExpandedCompetitors] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [loadingMore, setLoadingMore] = useState({});
  const [periodosDisponibles, setPeriodosDisponibles] = useState([]);
  const [selectedPeriodos, setSelectedPeriodos] = useState([]);

  useEffect(() => {
    loadBrandProfiles();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      setSelectedPeriodos([]);
      loadTrackerSummary(selectedBrandId, []);
    }
  }, [selectedBrandId]);

  const loadBrandProfiles = async () => {
    try {
      setLoading(true);
      const res = await camdobrainService.getBrandProfiles();
      const profiles = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setBrandProfiles(profiles);
      if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }
    } catch (err) {
      console.error('Error loading brand profiles:', err);
      setError('Error al cargar perfiles de marca');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackerSummary = async (brandId, periodos) => {
    try {
      setLoadingData(true);
      setError(null);
      const periodosStr = (periodos || []).join(',');
      const res = await camdobrainService.getCompetitorTrackerSummary(brandId, POSTS_PER_PAGE, 0, periodosStr);
      setData(res.data);
      if (res.data.periodos_disponibles) {
        setPeriodosDisponibles(res.data.periodos_disponibles);
      }
      const expanded = {};
      (res.data?.competitors || []).forEach((c, i) => {
        expanded[c.id] = i === 0;
      });
      setExpandedCompetitors(expanded);
    } catch (err) {
      console.error('Error loading tracker summary:', err);
      setError(err.response?.data?.error || 'Error al cargar datos de competencia');
      setData(null);
    } finally {
      setLoadingData(false);
    }
  };

  const loadMorePosts = useCallback(async (competitorId) => {
    if (loadingMore[competitorId]) return;

    const comp = data?.competitors?.find(c => c.id === competitorId);
    if (!comp) return;

    const currentCount = comp.posts?.length || 0;
    if (currentCount >= comp.total_posts) return;

    try {
      setLoadingMore(prev => ({ ...prev, [competitorId]: true }));
      const periodosStr = selectedPeriodos.join(',');
      const res = await camdobrainService.getCompetitorTrackerSummary(
        selectedBrandId, POSTS_PER_PAGE, currentCount, periodosStr
      );
      const newComp = res.data?.competitors?.find(c => c.id === competitorId);
      if (newComp?.posts?.length > 0) {
        setData(prev => ({
          ...prev,
          competitors: prev.competitors.map(c =>
            c.id === competitorId
              ? { ...c, posts: [...c.posts, ...newComp.posts] }
              : c
          ),
        }));
      }
    } catch (err) {
      console.error('Error loading more posts:', err);
    } finally {
      setLoadingMore(prev => ({ ...prev, [competitorId]: false }));
    }
  }, [data, selectedBrandId, loadingMore, selectedPeriodos]);

  const handlePeriodoToggle = (periodo) => {
    setSelectedPeriodos(prev => {
      const next = prev.includes(periodo)
        ? prev.filter(p => p !== periodo)
        : [...prev, periodo];
      loadTrackerSummary(selectedBrandId, next);
      return next;
    });
  };

  const handleClearPeriodos = () => {
    setSelectedPeriodos([]);
    loadTrackerSummary(selectedBrandId, []);
  };

  const toggleCompetitor = (id) => {
    setExpandedCompetitors(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPeriodoLabel = (periodo) => {
    const [year, month] = periodo.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  };

  const getMediaTypeLabel = (type) => {
    const labels = {
      'IMAGE': 'Imagen',
      'VIDEO': 'Video',
      'REELS': 'Reel',
      'CAROUSEL_ALBUM': 'Carrusel',
    };
    return labels[type] || type || '-';
  };

  const getMediaTypeBadgeClass = (type) => {
    const classes = {
      'IMAGE': 'badge-image',
      'VIDEO': 'badge-video',
      'REELS': 'badge-reel',
      'CAROUSEL_ALBUM': 'badge-carousel',
    };
    return classes[type] || 'badge-default';
  };

  // Totales globales filtrados
  const globalTotals = useMemo(() => {
    if (!data?.competitors) return { likes: 0, comments: 0, posts: 0 };
    return data.competitors.reduce((acc, comp) => ({
      likes: acc.likes + (comp.total_likes || 0),
      comments: acc.comments + (comp.total_comments || 0),
      posts: acc.posts + (comp.total_posts || 0),
    }), { likes: 0, comments: 0, posts: 0 });
  }, [data]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="competencia-tracker-page">
      {/* Header */}
      <div className="ct-header">
        <div className="ct-header-left">
          <button className="ct-back-btn" onClick={() => navigate(-1)}>
            &larr; Volver
          </button>
          <h1>Competencia - Tracker Posts</h1>
        </div>
        <div className="ct-header-actions">
          <select
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(Number(e.target.value))}
            className="ct-brand-select"
          >
            {brandProfiles.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
          <button
            className="ct-refresh-btn"
            onClick={() => selectedBrandId && loadTrackerSummary(selectedBrandId, selectedPeriodos)}
            disabled={loadingData}
          >
            {loadingData ? '...' : 'Actualizar'}
          </button>
        </div>
      </div>

      {/* Filtro de periodos */}
      {periodosDisponibles.length > 0 && (
        <div className="ct-filter-bar">
          <div className="ct-filter-label">Filtrar por periodo:</div>
          <div className="ct-filter-chips">
            {periodosDisponibles.map(periodo => (
              <button
                key={periodo}
                className={`ct-chip ${selectedPeriodos.includes(periodo) ? 'ct-chip-active' : ''}`}
                onClick={() => handlePeriodoToggle(periodo)}
                disabled={loadingData}
              >
                {formatPeriodoLabel(periodo)}
              </button>
            ))}
            {selectedPeriodos.length > 0 && (
              <button
                className="ct-chip ct-chip-clear"
                onClick={handleClearPeriodos}
                disabled={loadingData}
              >
                Limpiar filtros
              </button>
            )}
          </div>
          {selectedPeriodos.length > 0 && (
            <div className="ct-filter-summary">
              <span className="ct-filter-summary-item">
                <strong>{globalTotals.posts}</strong> posts
              </span>
              <span className="ct-filter-summary-item ct-filter-likes">
                &#10084; <strong>{formatNumber(globalTotals.likes)}</strong> likes
              </span>
              <span className="ct-filter-summary-item ct-filter-comments">
                &#128172; <strong>{formatNumber(globalTotals.comments)}</strong> comentarios
              </span>
              <span className="ct-filter-summary-label">
                en {selectedPeriodos.length === 1 ? 'el periodo' : 'los periodos'} seleccionado{selectedPeriodos.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ct-error-banner">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Loading data */}
      {loadingData && <Loading fullScreen={false} mensaje="Cargando datos de competidores" />}

      {/* No data */}
      {!loadingData && data && data.competitors?.length === 0 && (
        <div className="ct-no-data">
          <h2>Sin competidores configurados</h2>
          <p>Agrega competidores en el perfil de marca para ver sus posts.</p>
        </div>
      )}

      {/* Competitors list */}
      {!loadingData && data && data.competitors?.length > 0 && (
        <div className="ct-competitors-list">
          {data.competitors.map(comp => (
            <div key={comp.id} className="ct-competitor-section">
              {/* Competitor header */}
              <div
                className="ct-competitor-header"
                onClick={() => toggleCompetitor(comp.id)}
              >
                <div className="ct-competitor-info">
                  {comp.profile_picture_url ? (
                    <img
                      src={comp.profile_picture_url}
                      alt={comp.username}
                      className="ct-avatar"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="ct-avatar-placeholder">
                      {comp.username?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <span className="ct-username">@{comp.username}</span>
                    {comp.display_name && (
                      <span className="ct-display-name">{comp.display_name}</span>
                    )}
                  </div>
                </div>
                <div className="ct-competitor-meta">
                  <span className="ct-followers">
                    {formatNumber(comp.followers_count)} seguidores
                  </span>
                  <span className="ct-meta-stat ct-total-likes" title="Total likes de posts trackeados">
                    <span className="ct-meta-icon">&#10084;</span>
                    {formatNumber(comp.total_likes)}
                  </span>
                  <span className="ct-meta-stat ct-total-comments" title="Total comentarios de posts trackeados">
                    <span className="ct-meta-icon">&#128172;</span>
                    {formatNumber(comp.total_comments)}
                  </span>
                  <span className="ct-post-count">
                    {comp.total_posts || comp.posts?.length || 0} posts
                  </span>
                  <span className={`ct-expand-icon ${expandedCompetitors[comp.id] ? 'expanded' : ''}`}>
                    &#9660;
                  </span>
                </div>
              </div>

              {/* Posts table */}
              {expandedCompetitors[comp.id] && (
                <div className="ct-posts-container">
                  {comp.posts?.length === 0 ? (
                    <div className="ct-no-posts">
                      {selectedPeriodos.length > 0
                        ? 'Sin posts en los periodos seleccionados'
                        : 'Sin posts registrados'}
                    </div>
                  ) : (
                    <>
                      <div className="ct-posts-table-wrap">
                        <table className="ct-posts-table">
                          <thead>
                            <tr>
                              <th className="ct-th-thumb"></th>
                              <th>Periodo</th>
                              <th>Tipo</th>
                              <th>Caption</th>
                              <th className="ct-th-right">Likes</th>
                              <th className="ct-th-right">Comentarios</th>
                              <th>Publicación</th>
                              <th>Snapshot</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {comp.posts.map((post, idx) => (
                              <tr key={post.post_id || idx}>
                                <td className="ct-td-thumb">
                                  {(post.thumbnail_url || post.media_url) ? (
                                    <img
                                      src={post.thumbnail_url || post.media_url}
                                      alt=""
                                      className="ct-table-img"
                                      loading="lazy"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  ) : (
                                    <div className="ct-table-img-placeholder">-</div>
                                  )}
                                </td>
                                <td className="ct-td-periodo">
                                  {post.timestamp
                                    ? new Date(post.timestamp).toLocaleDateString('es-CL', { month: 'short', year: 'numeric' })
                                    : '-'}
                                </td>
                                <td>
                                  <span className={`ct-media-badge ${getMediaTypeBadgeClass(post.media_type)}`}>
                                    {getMediaTypeLabel(post.media_type)}
                                  </span>
                                </td>
                                <td className="ct-td-caption">
                                  {post.caption
                                    ? post.caption.substring(0, 100) + (post.caption.length > 100 ? '...' : '')
                                    : <span className="ct-muted">Sin caption</span>}
                                </td>
                                <td className="ct-td-metric">
                                  {formatNumber(post.like_count)}
                                </td>
                                <td className="ct-td-metric">
                                  {formatNumber(post.comments_count)}
                                </td>
                                <td className="ct-td-date">{formatDate(post.timestamp)}</td>
                                <td className="ct-td-date">{formatDateTime(post.snapshot_date)}</td>
                                <td>
                                  {post.permalink && (
                                    <a
                                      href={post.permalink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ct-table-link"
                                    >
                                      Ver
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Load more button */}
                      {comp.total_posts > (comp.posts?.length || 0) && (
                        <div className="ct-load-more">
                          <button
                            className="ct-load-more-btn"
                            onClick={() => loadMorePosts(comp.id)}
                            disabled={loadingMore[comp.id]}
                          >
                            {loadingMore[comp.id]
                              ? 'Cargando...'
                              : `Cargar más posts (${comp.posts.length} de ${comp.total_posts})`
                            }
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompetenciaTrackerPage;
